import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Navigation from "./components/Navigation";
import BlindsLayer from "./components/BlindsLayer";
import CustomCursor from "./components/CustomCursor";
import BookingForm from "./components/BookingForm";
import Footer from "./components/Footer";
import CollectionsSection from "./components/CollectionsSection";
import ProcessSection from "./components/ProcessSection";
import GallerySection from "./components/GallerySection";
import StatsSection from "./components/StatsSection";
import TestimonialsSection from "./components/TestimonialsSection";
import Marquee from "@/components/base/Marquee";
import { lumiereSections } from "@/mocks/sections";

// Each section reserves this many viewport heights of scroll
const SCROLL_PER_SECTION = 1.4;

export default function Home() {
  const sections = lumiereSections;
  const sectionCount = sections.length;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [scrollHinted, setScrollHinted] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [layerProgress, setLayerProgress] = useState<number[]>(() => sections.map((_, i) => (i === 0 ? 1 : 0)));
  const [isMobile, setIsMobile] = useState(false);

  // Total pinned scroll height for the blinds sequence (in vh units)
  const totalScrollVh = useMemo(() => sectionCount * SCROLL_PER_SECTION, [sectionCount]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const totalScrollPx = (rect.height * (totalScrollVh - 1)) / totalScrollVh; // usable scroll range while pinned (CSS-vh based)
    const start = -rect.top; // how far we've scrolled into the container
    const raw = Math.max(0, Math.min(totalScrollPx, start));
    const tNorm = totalScrollPx > 0 ? raw / totalScrollPx : 0;

    // Each section transition occupies 1/(N-1) of the normalized range (transitions between layers)
    const transitions = sectionCount - 1;
    const perTrans = transitions > 0 ? 1 / transitions : 1;

    const progresses = sections.map((_, i) => {
      if (i === 0) return 1;
      const transStart = (i - 1) * perTrans;
      const local = (tNorm - transStart) / perTrans;
      return Math.max(0, Math.min(1, local));
    });
    setLayerProgress(progresses);

    // Determine the active section based on highest progress >= 0.5
    let active = 0;
    for (let i = sectionCount - 1; i >= 0; i--) {
      if (progresses[i] >= 0.5) {
        active = i;
        break;
      }
    }
    setActiveIndex(active);

    // Progress bar — accounts for everything below too
    const docScrollable = document.documentElement.scrollHeight - window.innerHeight;
    const docPct = docScrollable > 0 ? (window.scrollY / docScrollable) * 100 : 0;
    setProgressPct(docPct);

    if (window.scrollY > 80 && !scrollHinted) setScrollHinted(true);
  }, [sectionCount, sections, totalScrollVh, scrollHinted]);

  useEffect(() => {
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [handleScroll]);

  const scrollToSection = useCallback(
    (id: string) => {
      // If the target lives below the pinned blinds hero (any element with this id
      // that ISN'T one of the blinds scenes), just scroll the element into view.
      const isBlindsScene = sections.some((s) => s.id === id);
      if (!isBlindsScene) {
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 80; // offset for fixed nav
          window.scrollTo({ top, behavior: "smooth" });
        }
        return;
      }

      const idx = sections.findIndex((s) => s.id === id);
      if (idx < 0) return;

      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const transitions = sectionCount - 1;
      const perTrans = transitions > 0 ? 1 / transitions : 1;
      // Clamp slightly inside the boundary so the target section sits firmly in view
      const tNorm = idx === 0 ? 0 : Math.min(0.9995, idx * perTrans);
      const totalScrollPx = (rect.height * (totalScrollVh - 1)) / totalScrollVh;
      const targetWithinContainer = tNorm * totalScrollPx;
      const containerTop = rect.top + window.scrollY;
      const targetY = Math.max(0, containerTop + targetWithinContainer);

      window.scrollTo({ top: targetY, behavior: "smooth" });
    },
    [sections, sectionCount, totalScrollVh]
  );

  return (
    <div className="relative bg-[var(--lum-bg)] text-[var(--lum-cream)] lum-shell font-jp">
      <CustomCursor />
      <Navigation />

      {/* Pinned blinds scene */}
      <div
        ref={containerRef}
        id="intro"
        className="relative w-full"
        style={{ height: `${totalScrollVh * 100}vh` }}
      >
        <div className="sticky top-0 h-screen w-full overflow-hidden">
          {/* Base color */}
          <div className="absolute inset-0 bg-white" />

          {sections.map((s, i) => (
            <BlindsLayer
              key={s.id}
              section={s}
              index={i}
              progress={layerProgress[i] ?? 0}
              isFirst={i === 0}
              isMobile={isMobile}
              onNavigate={scrollToSection}
            />
          ))}

          {/* Section counter */}
          <div className="absolute bottom-8 right-6 md:right-12 z-40 font-label text-[11px] text-[var(--lum-cream)]/85 pointer-events-none">
            <span className="text-[var(--lum-gold)]">
              {String(activeIndex + 1).padStart(2, "0")}
            </span>
            <span className="mx-2 text-[var(--lum-cream)]/30">/</span>
            <span>{String(sectionCount).padStart(2, "0")}</span>
          </div>

          {/* Vertical pagination dots */}
          <div className="hidden md:flex absolute right-12 top-1/2 -translate-y-1/2 z-40 flex-col items-center gap-4">
            {sections.map((s, i) => (
              <button
                key={s.id}
                onClick={() => scrollToSection(s.id)}
                aria-label={`跳转到 ${s.label}`}
                className="group relative w-3 h-3 flex items-center justify-center cursor-pointer"
              >
                <span
                  className={`block rounded-full transition-all duration-500 ${
                    activeIndex === i
                      ? "w-2 h-2 bg-[var(--lum-gold)]"
                      : "w-1.5 h-1.5 bg-[var(--lum-cream)]/30 group-hover:bg-[var(--lum-cream)]/70"
                  }`}
                />
              </button>
            ))}
          </div>

          {/* Scroll hint */}
          <div
            className={`absolute bottom-10 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-2 transition-opacity duration-700 pointer-events-none ${
              scrollHinted ? "opacity-0" : "opacity-100"
            }`}
          >
            <span className="font-label text-[10px] text-[var(--lum-cream)]/70">SCROLL</span>
            <i className="ri-arrow-down-line w-5 h-5 flex items-center justify-center text-[var(--lum-gold)] lum-bob"></i>
          </div>

          {/* Scroll progress bar (1px gold line) */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-white/5 z-40">
            <div
              className="h-full lum-progress transition-[width] duration-150"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Editorial content sections below the blinds scene */}
      <main>
        {/* Marquee ribbon */}
        <div className="bg-[#2563eb] py-6 md:py-7 border-t border-b border-white/15 text-white">
          <Marquee
            items={[
              "社長の放課後部",
              "「社長」をちょっと休みに来る部活",
              "他己紹介から始まります",
              "事業が噛み合わなくても続く関係",
              "人脈ではなく信頼",
              "体験入部 ¥9,500",
              "シーシャ代・ドリンク2杯分込み",
              "紙巻きたばこも吸えます",
            ]}
            speedSec={48}
          />
        </div>

        <CollectionsSection />

        {/* Hairline separator with travelling gold sweep */}
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="lum-rule my-2" />
        </div>

        <ProcessSection />

        {/* Reverse-direction marquee */}
        <div className="bg-[#2563eb] py-6 md:py-7 border-t border-b border-white/15 text-white">
          <Marquee
            items={[
              "放課後の時間割 · 他己紹介",
              "今日持って帰れる話",
              "最近のへぇ · 自由時間",
              "ルールはひとつだけ",
              "少人数制",
            ]}
            speedSec={52}
            reverse
          />
        </div>

        <GallerySection />
        <StatsSection />
        <TestimonialsSection />
        <BookingForm />
      </main>
      <Footer />
    </div>
  );
}